import { Box, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { exportClassSessionsAuditReport } from '../../../utils/auditing-export-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import CustomTable from '../../common/custom-table/CustomTable'
import LineBarGraph from '../../common/LineBarGraph'
import { Icons } from '../../../utils/utilities'
import api from '../../../service/api'


const columns =
    [
        { id: 1, field: "index", index: true, name: 'Sl.No.', width: "50px", align: "center", padding: "10px 20px" },
        { id: 2, field: "faculty_name", filterable: true, sortable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.empcode}?legacy=${row.legacy_system_data}`, renderCell: row => row?.faculty_name?.toLowerCase() || row?.empcode, cellStyles: { textTransform: "capitalize" }, name: "Faculty", width: "150px", padding: "10px 20px" },
        { id: 3, field: "empcode", filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.empcode}?legacy=${row.legacy_system_data}`, name: "Empcode", padding: "10px 20px" },
        { id: 6, field: "department", filterable: true, renderCell: row => `${row.institute} - ${row.department_name?.toLowerCase()}`, cellStyles: { textTransform: "capitalize" }, name: "Department", width: "150px", padding: "10px 20px" },
        { id: 4, field: "live_sessions_count", filterable: true, align: "center", style: { align: "center" }, name: "Class Sessions", width: "150px", padding: "10px 20px" },
        { id: 6, field: "resources_count", filterable: true, align: "center", style: { align: "center" }, name: "Resources", width: "150px", padding: "10px 20px" },
    ]


const ClassSessionsStats = () => {

    const [searchParams] = useSearchParams( { sortByDept: "0", sortByEmpCode: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortByResourcesCount: "0", sortByAttachmentCount: "0", sortByClassSessionsCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const searchText = searchParams.get( 'searchText' ) || ""
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [facultyStats, setFacultyStats] = useState( [] )
    const [last12MonthsStats, setLast12MonthsStats] = useState( [] )
    const [loadingGraphData, setLoadingGraphData] = useState( true )
    const [sortedFacultyStats, setSortedFacultyStats] = useState( [] )

    const { layout, palette } = useTheme()

    const searchFilterFunc = item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( item.legacy_system_data && +legacy_department_id === +item.department_id ) ||
                ( !item.legacy_system_data && +department_id === +item.department_id )
            return doesMatchDepartment && ( item?.department?.match( searchValue ) || item?.faculty_name?.match( searchValue ) || item.empcode?.match( searchValue ) )
        } else
            return item?.department?.match( searchValue ) || item?.faculty_name?.match( searchValue ) || item.empcode?.match( searchValue )
    }


    const getStats = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { facultyStats = [], last12MonthsStats } } } = await api.audit.getClassSessionsStats( { month, institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setFacultyStats( facultyStats )
            setSortedFacultyStats( facultyStats )
            setLast12MonthsStats( Object.keys( last12MonthsStats || {} ).length > 0 ? last12MonthsStats : null )
            setError( null )
        } catch ( err ) {
            console.log( "STATTS ERR", err )
            setError( { code: err?.response?.status || 500, message: err?.response?.data?.message || "Something went wrong while fetching the class sessions audit data.", retryFunction: getStats } )
        } finally {
            setLoading( false )
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute] )

    useEffect( () => {
        getStats()
    }, [getStats] )

    useEffect( () => {
        const getGraphData = async () => {
            try {
                setLoadingGraphData( true )
                const institute = selectedInstitute !== "all" ? selectedInstitute : ""
                const department = selectedDepartment !== "all" ? selectedDepartment : ""
                const { data: graphDetails } = await api.audit.getClassSessionsGraphData( { month, institute, department } )
                setGraphData( graphDetails.data )
                setError( null )
            } catch ( err ) {
                console.log( err )
                setError( { retryFunction: getGraphData, code: err?.response?.status || 500, message: err?.response?.data?.message || "Something went wrong while fetching the class sessions graph data." } )
            } finally {
                setLoadingGraphData( false )
            }
        }
        getGraphData()
    }, [month, selectedInstitute, selectedDepartment] )

    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>
            {/* Filters */}
            <AuditPageStatsFilterBar showZeroResourcesSwitch={false} resource="class resources" listResourceBtnLabel="View Class Resources" title="Class resources statistics" />


            {!loading && !error && view === 'tabular' && <CustomTable
                globalSearch="Search by faculty name or employee code..."
                hasFilters={selectedDepartment !== "all" || selectedInstitute !== "all"}
                pageSize={50}
                columns={columns}
                data={facultyStats}
                tableStyles={{
                    maxHeight: "450px"
                }}
                customActions={[
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        onClick: ( rows ) => exportClassSessionsAuditReport( rows, last12MonthsStats, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-classsessions-audit-report` )
                    }
                ]}
            />}
            {!loading && !error && view === 'tabular' && facultyStats.length > 0 && <Typography padding="20px" paddingTop="0" fontSize="12px"> <em>*Live sessions are only considered to count if they contain resources</em> </Typography>}

            {/* Graph */}
            {
                !loading && !error && !loadingGraphData && view !== 'tabular' && sortedFacultyStats.filter( searchFilterFunc ).length > 0 &&
                <Box display="flex" color={palette.common.font} flexGrow={1} minHeight="400px">
                    <LineBarGraph lineColor='#035efc' barColor='#FEE715' showMenuBar={false} yAxisName="Resources" xAxisName="Date" lineLegend='Resources' dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'resources_added_count'} lineData={graphData} />
                </Box>
            }
            {/* Retry */}
            {error && <TryAgainMessageBlock err={error.message} loading={loading} code={error.code} getData={error.retryFunction} />}

            {/* Loading */}
            {( loading || loadingGraphData ) && !error && <DataLoadingSpinner padding="0" waitingMessage="Loading class sessions audit report please..." />}
        </Box >
    )
}

export default ClassSessionsStats
